import _ from 'lodash';
import {
  setPaginationElementsCount,
  setPaginationHasPreviousAndHasNext
} from 'ducks/pagination';
import {
  ExtraAction,
  FetchActionCreator,
  PostActionCreator,
  ActionCreatorChild
} from 'ducks/types';

type InitialAction = (
  data?: any,
  successExtraActions?: ExtraAction[],
  failureExtraActions?: ExtraAction[]
) => {payload: {}};

// This function will set .isLoading to true when the fetch action is dispatched
export const defaultInitialActionReducer = actionCreator => state => {
  if (actionCreator.stateKey) {
    return _.set(
      _.cloneDeep(state),
      `${actionCreator.stateKey}.isLoading`,
      true
    );
  }
  return state;
};

export const defaultSuccessReducer = (state, action) => {
  const actionStateChange = {
    isLoading: false,
    data: action.payload
  };

  // This function supports stateKey with "dot" notation
  // For example you can pass "schedules.rent" and `_.set` will take care of the nesting
  // We use _.cloneDeep, because _.set mutates and we don't want to mutate the state

  if (action.parent.stateKey) {
    return _.set(_.cloneDeep(state), action.parent.stateKey, actionStateChange);
  }
  return state;
};

export const defaultFailureReducer = (state, action) => {
  const actionStateChange = {
    isLoading: false,
    errors: action.payload.errors
  };

  // This function supports stateKey with "dot" notation
  // For example you can pass "schedules.rent" and `_.set` will take care of the nesting
  // We use _.cloneDeep, because _.set mutates and we don't want to mutate the state

  if (action.parent.stateKey) {
    return _.set(_.cloneDeep(state), action.parent.stateKey, actionStateChange);
  }

  return state;
};

const defaultNotFoundReducer = (state, action) => {
  const actionStateChange = {
    isLoading: false,
    notFound: true
  };

  // This function supports stateKey with "dot" notation
  // For example you can pass "schedules.rent" and `_.set` will take care of the nesting
  // We use _.cloneDeep, because _.set mutates and we don't want to mutate the state

  if (action.parent.stateKey) {
    return _.set(_.cloneDeep(state), action.parent.stateKey, actionStateChange);
  }

  return state;
};

const createChildAction: (
  name: string,
  parentAction: FetchActionCreator | PostActionCreator
) => ActionCreatorChild = (name, parentAction) => {
  return {
    type: name,
    parent: parentAction
  };
};

const createFetchAction = (
  name,
  namespace,
  initialAction: InitialAction,
  stateKey,
  defaultStateValue = {},
  extraGetParamsSelectors: any[] = [] // Used for filters + sorting + pagination etc.
) => {
  const action: FetchActionCreator = (
    data?: any,
    extraActions?: ExtraAction[]
  ) => ({
    type: name,
    ...initialAction(data, extraActions)
  });

  action.type = name;
  action.namespace = namespace;
  action.defaultStateValue = defaultStateValue;
  action.extraGetParamsSelectors = extraGetParamsSelectors;

  if (stateKey) {
    action.stateKey = stateKey;
  }

  action.success = createChildAction(`${name}_SUCCESS`, action);
  action.failure = createChildAction(`${name}_FAILURE`, action);
  action.notFound = createChildAction(`${name}_NOTFOUND`, action);

  return action;
};

export const createReducer = (initialState, actionHandlers) => {
  return (state = initialState, action) => {
    if (actionHandlers[action.type]) {
      return actionHandlers[action.type](state, action);
    }
    return state;
  };
};

export const createFetchReducers = (
  actionCreator,
  successReducer = defaultSuccessReducer,
  failureReducer = defaultFailureReducer,
  notFoundReducer = defaultNotFoundReducer
) => ({
  [actionCreator.type]: defaultInitialActionReducer(actionCreator),
  [actionCreator.success.type]: successReducer,
  [actionCreator.failure.type]: failureReducer,
  [actionCreator.notFound.type]: notFoundReducer
});

export const createFetchActionForNamespace = namespace => ({
  name,
  initialAction,
  stateKey = undefined,
  defaultStateValue = {},
  extraGetParamsSelectors = []
}: {
  name: string;
  initialAction: any;
  stateKey?: string;
  defaultStateValue?: any;
  extraGetParamsSelectors?: any[];
}) =>
  createFetchAction(
    `${namespace}/${name}`,
    namespace,
    initialAction,
    stateKey,
    defaultStateValue,
    extraGetParamsSelectors
  );

export const noDataForAction = () => ({payload: {}});

export const createFetchSelectors = (
  action: FetchActionCreator | PostActionCreator
) => {
  const search = '/';
  const replace = '.';
  const normalizedNamespace = action.namespace.replace(
    new RegExp(search, 'g'),
    replace
  );

  const storePath = `${normalizedNamespace}.${action.stateKey}`;

  const data = state => {
    return _.get(state, `${storePath}.data`, action.defaultStateValue);
  };
  const errors = state => _.get(state, `${storePath}.errors`, {});

  const isLoading = state => {
    return _.get(state, `${storePath}.isLoading`, false);
  };

  return {data, errors, isLoading};
};

const createPostAction = (
  name,
  namespace,
  initialAction: InitialAction,
  stateKey,
  defaultStateValue = {},
  formName
) => {
  const action: PostActionCreator = (
    data?: any,
    successExtraActions?: ExtraAction[],
    failureExtraActions?: ExtraAction[]
  ) => ({
    type: name,
    ...initialAction(data, successExtraActions, failureExtraActions)
  });

  action.type = name;
  action.namespace = namespace;
  action.defaultStateValue = defaultStateValue;
  action.formName = formName;

  if (stateKey) {
    action.stateKey = stateKey;
  }

  action.success = createChildAction(`${name}_SUCCESS`, action);
  action.failure = createChildAction(`${name}_FAILURE`, action);
  action.notFound = createChildAction(`${name}_NOTFOUND`, action);

  return action;
};

export const createPostActionForNamespace = namespace => ({
  name,
  initialAction,
  stateKey = undefined,
  defaultStateValue = {},
  formName = ''
}: any & {stateKey?: string}) =>
  createPostAction(
    `${namespace}/${name}`,
    namespace,
    initialAction,
    stateKey,
    defaultStateValue,
    formName
  );

// The idea is to use these utils during the refactoring.
// After we finish we'll check all the places we use them and maybe combine `fetch` and `post` utils into one.
export const createPostReducers = createFetchReducers;
export const createPostSelectors = createFetchSelectors;

export const createFetchPaginatedActionForNamespace = namespace => ({
  name,
  initialAction,
  stateKey,
  defaultStateValue,
  extraGetParamsSelectors, // Used for filters + sorting etc.
  paginationNamespace
}) =>
  createFetchPaginatedAction(
    `${namespace}/${name}`,
    namespace,
    initialAction,
    stateKey,
    defaultStateValue,
    extraGetParamsSelectors,
    paginationNamespace
  );

const createFetchPaginatedAction = (
  name: string,
  namespace,
  initialAction: InitialAction,
  stateKey,
  defaultStateValue = {},
  extraGetParamsSelectors = [], // Used for filters + sorting + pagination etc.
  paginationNamespace
) => {
  const paginationExtraAction = data =>
    setPaginationElementsCount({
      elementsCount: data.count,
      namespace: paginationNamespace
    });

  const action: FetchActionCreator = (
    data?: any,
    extraActions: ExtraAction[] = []
  ) => {
    const obj = {
      type: name,
      ...initialAction(data, extraActions)
    };

    return _.set(obj, 'payload.extraActions', [
      ...extraActions,
      paginationExtraAction
    ]);
  };

  action.type = name;
  action.namespace = namespace;
  action.defaultStateValue = defaultStateValue;
  action.extraGetParamsSelectors = extraGetParamsSelectors;

  if (stateKey) {
    action.stateKey = stateKey;
  }

  action.success = createChildAction(`${name}_SUCCESS`, action);
  action.failure = createChildAction(`${name}_FAILURE`, action);
  action.notFound = createChildAction(`${name}_NOTFOUND`, action);

  return action;
};

const defaultSuccessReducerWithPagination = (state, action) => ({
  ...state,
  [action.parent.stateKey]: {
    data: action.payload.results,
    isLoading: false
  }
});

export const createFetchReducersWithPagination = (
  actionCreator,
  successReducer = defaultSuccessReducerWithPagination,
  failureReducer = defaultFailureReducer
) => ({
  [actionCreator.type]: defaultInitialActionReducer(actionCreator),
  [actionCreator.success.type]: successReducer,
  [actionCreator.failure.type]: failureReducer
});

export const createFetchCursorPaginatedActionForNamespace = namespace => ({
  name,
  initialAction,
  stateKey,
  defaultStateValue,
  extraGetParamsSelectors, // Used for filters + sorting etc.
  paginationNamespace
}) =>
  createFetchCursorPaginatedAction(
    `${namespace}/${name}`,
    namespace,
    initialAction,
    stateKey,
    defaultStateValue,
    extraGetParamsSelectors,
    paginationNamespace
  );

const createFetchCursorPaginatedAction = (
  name: string,
  namespace,
  initialAction: InitialAction,
  stateKey,
  defaultStateValue = {},
  extraGetParamsSelectors = [], // Used for filters + sorting + pagination etc.
  paginationNamespace
) => {
  const paginationExtraAction = data =>
    setPaginationHasPreviousAndHasNext({
      hasPrevious: data.meta.hasPrevious,
      hasNext: data.meta.hasNext,
      namespace: paginationNamespace
    });

  const action: FetchActionCreator = (
    data?: any,
    extraActions: ExtraAction[] = []
  ) => {
    const obj = {
      type: name,
      ...initialAction(data, extraActions)
    };

    return _.set(obj, 'payload.extraActions', [
      ...extraActions,
      paginationExtraAction
    ]);
  };

  action.type = name;
  action.namespace = namespace;
  action.defaultStateValue = defaultStateValue;
  action.extraGetParamsSelectors = extraGetParamsSelectors;

  if (stateKey) {
    action.stateKey = stateKey;
  }

  action.success = createChildAction(`${name}_SUCCESS`, action);
  action.failure = createChildAction(`${name}_FAILURE`, action);
  action.notFound = createChildAction(`${name}_NOTFOUND`, action);

  return action;
};
